import React, { FC, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStore,
  faShop,
  faUser,
  faTicket,
  faPercent,
  faSignOut,
  faHome,
  faEye,
  faEyeSlash,
  faAward,
  faLayerGroup,
  faBox,
  faWarehouse,
  faClipboardList,
  faStar,
  faTags,
  faLocationDot,
  faCashRegister,
  faBars,
  faTag,
  faBullhorn,
  faUsers,
  faTriangleExclamation,
  faChartBar,
  faRightFromBracket,
  faEnvelope,
  faPhone,
  faIdCard,
  faGear,
  faBell,
  faCoins,
  faClock
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { resolveImageUrl, API_BASE } from "../../config/api";
import { authenticatedFetch } from "../../utils/api";
import NotificationBell from "../NotificationBell";
import Backdrop from "../Backdrop";
import BrandMark from "../BrandMark";
import style from "./style.module.css";

interface SidebarProps {
  className?: string;
  isMobile?: boolean;
}

const ROLE_LABELS: Record<string, string> = { O: "Owner", A: "Admin", S: "Sales", C: "Customer" };
const ROLE_COLORS: Record<string, string> = { O: "#7c3aed", A: "#0369a1", S: "#0f766e", C: "#6b7280" };

const Sidebar: FC<SidebarProps> = ({ className, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHidden, setIsHidden] = useState(isMobile);
  const [sidebarWidth] = useState(240);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const auth = cookies.auth || {};
  // "Delivery Slots" and "Customers" are additionally gated for an Admin by
  // whether any of their owned stores are non-pos_only — Owner is unrestricted (matches
  // every other ownership check in this codebase) and Sales already can't
  // reach this item via its roles list. null = not yet resolved, so the
  // item stays hidden until known rather than flashing then disappearing;
  // every other nav item is unaffected and renders immediately as today.
  // Reuses the existing, already-ownership-scoped GET /store endpoint —
  // no new backend endpoint, and deliberately not cached from login (see
  // docs/store-channel-access-control-plan.md).
  const [hasEligibleStore, setHasEligibleStore] = useState<boolean | null>(null);

  useEffect(() => {
    if (!permissions.isAdmin) return;
    let cancelled = false;
    authenticatedFetch(`${API_BASE}/store?limit=100`, { method: 'GET' }, cookies.auth)
      .then(res => (res.ok ? res.json() : { data: [] }))
      .then(data => {
        if (cancelled) return;
        const stores = Array.isArray(data?.data) ? data.data : [];
        setHasEligibleStore(stores.some((s: any) => s.channel_type !== 'pos_only'));
      })
      .catch(() => { if (!cancelled) setHasEligibleStore(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.isAdmin]);
  const fullName = [auth.first_name, auth.last_name].filter(Boolean).join(" ") || "User";
  const initials = [auth.first_name?.[0], auth.last_name?.[0]].filter(Boolean).join("").toUpperCase() || "U";
  const roleLabel = ROLE_LABELS[auth.user_type] || auth.user_type || "";
  const roleColor = ROLE_COLORS[auth.user_type] || "#6b7280";
  const avatarSrc = auth.profile_image ? resolveImageUrl(auth.profile_image) : null;

  // When switching between mobile/desktop reset hidden state
  useEffect(() => {
    setIsHidden(isMobile);
    window.dispatchEvent(new CustomEvent('sidebarVisibilityChange', { detail: isMobile }));
  }, [isMobile]);

  const allNavItems = [
    { name: "Dashboard", route: "/dashboard", icon: faHome,               roles: ['O', 'A', 'S'], group: "Main" },
    { name: "Reports",   route: "/reports",   icon: faChartBar,           roles: ['O', 'A'],       group: "Main" },
    { name: "Checkout",  route: "/checkout",  icon: faCashRegister,       roles: ['O', 'A', 'S'], group: "Main" },
    { name: "Stores",    route: "/stores",    icon: faStore,              roles: ['O', 'A', 'S'], group: "Catalog" },
    { name: "Shops",     route: "/shops",     icon: faShop,               roles: ['O', 'A', 'S'], group: "Catalog" },
    { name: "Brands",    route: "/brands",    icon: faAward,              roles: ['O', 'A', 'S'], group: "Catalog" },
    { name: "Categories",route: "/category",  icon: faLayerGroup,         roles: ['O', 'A', 'S'], group: "Catalog" },
    { name: "Products",  route: "/products",  icon: faBox,                roles: ['O', 'A', 'S'], group: "Catalog" },
    { name: "Store Discount",    route: "/store-discount",    icon: faPercent, roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Shop Discount",     route: "/shop-discount",     icon: faPercent, roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Brand Discount",    route: "/brand-discount",    icon: faTags,    roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Category Discount", route: "/category-discount", icon: faPercent, roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Product Discount",  route: "/product-discount",  icon: faTags,    roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Discounts", route: "/discounts", icon: faTag,                roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Offers",    route: "/offers",    icon: faBullhorn,           roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Coupons",   route: "/coupons",   icon: faTicket,             roles: ['O', 'A', 'S'], group: "Pricing" },
    { name: "Orders",    route: "/order",     icon: faClipboardList,      roles: ['O', 'A', 'S'], group: "Operations" },
    { name: "Notifications", route: "/notifications", icon: faBell,       roles: ['O', 'A', 'S'], group: "Operations" },
    { name: "Inventory", route: "/inventory", icon: faWarehouse,          roles: ['O', 'A', 'S'], group: "Operations" },
    { name: "Low Stock", route: "/low-stock", icon: faTriangleExclamation,roles: ['O', 'A', 'S'], group: "Operations" },
    { name: "Reviews",   route: "/review",    icon: faStar,               roles: ['O', 'A', 'S'], group: "Operations" },
    // Grouped with Operations (Sales-visible), not Admin (['O','A']-only
    // items below) — Address's own roles are ['O','A','S'], so keeping it
    // under "Admin" would show Sales a section literally titled "Admin"
    // containing only this one item. Kept adjacent to the other Operations
    // items so the group's label doesn't get split into two disjoint runs
    // (getSidebarItems' label rendering is purely array-order-based, not a
    // fixed group enum — see the showGroupLabel logic below).
    { name: "Address",   route: "/address",   icon: faLocationDot,        roles: ['O', 'A', 'S'], group: "Operations" },
    // O/A only — Sales has no visibility into the cash drawer at all, not
    // just write access (docs/pos-cashier-operations-plan.md).
    { name: "Cash Drawer", route: "/cash-drawer", icon: faCoins,          roles: ['O', 'A'],      group: "Operations" },
    // O/A only, same reasoning as Cash Drawer above — Sales doesn't manage
    // delivery slots (docs/location-gated-stores-and-delivery-slots-plan.md).
    { name: "Delivery Slots", route: "/delivery-slots", icon: faClock,    roles: ['O', 'A'],      group: "Operations" },
    // Same reasoning as Delivery Slots — a separate, admin-managed pickup
    // schedule rather than sharing delivery's slot list (docs/pickup-slots-plan.md).
    { name: "Pickup Slots", route: "/pickup-slots", icon: faClock,        roles: ['O', 'A'],      group: "Operations" },
    { name: "Users",     route: "/users",     icon: faUser,               roles: ['O', 'A'],      group: "Admin" },
    { name: "Customers", route: "/customers", icon: faUsers,              roles: ['O', 'A'],      group: "Admin" },
    { name: "Profile",   route: "/profile",   icon: faIdCard,             roles: ['O', 'A', 'S'], group: "Account" },
    { name: "Settings",  route: "/settings",  icon: faGear,               roles: ['O', 'A', 'S'], group: "Account" },
  ];

  // docs/store-channel-access-control-plan.md, Step 9 — same gate as
  // Delivery Slots, extended to Customers (a pos_only store has no real
  // mobile-customer relationship worth managing here).
  const CHANNEL_GATED_ITEMS = ["Delivery Slots", "Pickup Slots", "Customers"];

  const getSidebarItems = () => {
    return allNavItems.filter(item => {
      if (!item.roles.includes(permissions.userType)) return false;
      if (CHANNEL_GATED_ITEMS.includes(item.name) && permissions.userType === 'A') {
        return hasEligibleStore === true;
      }
      return true;
    });
  };

  const toggleHide = () => {
    const next = !isHidden;
    setIsHidden(next);
    window.dispatchEvent(new CustomEvent('sidebarVisibilityChange', { detail: next }));
  };

  const handleNavClick = (item: typeof allNavItems[0]) => {
    if (item.name === 'Logout') {
      setShowLogoutDialog(true);
    } else {
      navigate(item.route);
      // Auto-close drawer on mobile after navigation
      if (isMobile) {
        setIsHidden(true);
        window.dispatchEvent(new CustomEvent('sidebarVisibilityChange', { detail: true }));
      }
    }
  };

  const handleLogoutConfirm = () => {
    // Revoke the refresh token server-side so the session can't be silently renewed
    // after logout — best-effort; local state is cleared regardless of the result.
    axios.post(`${API_BASE}/user/pos-logout`, {}, { withCredentials: true }).finally(() => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('auth');
      document.cookie = 'auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      delete axios.defaults.headers.common['Authorization'];
      // replace, not push: every history entry from this session is behind
      // RoleGuard, which is now about to reject all of them (auth cookie
      // just got cleared above). Pushing '/auth' on top just adds one more
      // guarded-away entry underneath it for Back to silently redirect
      // through — replacing it removes one concrete layer of that stack.
      navigate('/auth', { replace: true });
      setShowLogoutDialog(false);
    });
  };

  const sidebarClasses = [
    style.sidebar,
    isHidden ? style.hidden : '',
    className || ''
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Logout Confirmation Dialog */}
      {showLogoutDialog && (
        <div className={style.logoutDialogOverlay}>
          <div className={style.logoutDialog}>
            <div className={style.logoutDialogHeader}>
              <h3>Confirm Logout</h3>
            </div>
            <div className={style.logoutDialogContent}>
              <p>Are you sure you want to logout?</p>
            </div>
            <div className={style.logoutDialogActions}>
              <span className={style.logoutDialogTextBtn} onClick={() => setShowLogoutDialog(false)}>No</span>
              <span className={style.logoutDialogTextBtn} onClick={handleLogoutConfirm}>Yes</span>
            </div>
          </div>
        </div>
      )}

      {/* Mobile backdrop — tapping it closes the drawer */}
      {isMobile && !isHidden && (
        <Backdrop zIndex={1099} onClick={toggleHide} />
      )}

      {/* Hamburger / show-sidebar button — visible when sidebar is hidden */}
      {isHidden && (
        <button
          className={style.showSidebarBtn}
          onClick={toggleHide}
          title="Open Menu"
        >
          <FontAwesomeIcon icon={isMobile ? faBars : faEye} />
        </button>
      )}

      <div className={sidebarClasses} style={!isMobile ? { width: `${sidebarWidth}px` } : undefined}>
        <div className={style.sidebarBrand}>
          <span className={style.brandDot}><BrandMark variant="dark" size={18} /></span>
          <span className={style.brandName}>1Markaz</span>
          <NotificationBell />
          <button
            className={style.sidebarHideToggle}
            onClick={toggleHide}
            title={isHidden ? 'Show Sidebar' : 'Hide Sidebar'}
          >
            <FontAwesomeIcon icon={isHidden ? faEye : faEyeSlash} />
          </button>
        </div>

        <div className={style.sidebarContent}>
          {getSidebarItems().map((item, index, arr) => {
            const isActive = location.pathname === item.route;
            const prevItem = arr[index - 1];
            const showGroupLabel = !prevItem || prevItem.group !== item.group;
            return (
              <React.Fragment key={index}>
                {showGroupLabel && (
                  <div className={style.sidebarGroupLabel}>{item.group}</div>
                )}
                <div
                  className={`${style.sidebarItem} ${isActive ? style.active : ''}`}
                  onClick={() => handleNavClick(item)}
                >
                  <span className={style.sidebarIconWrap}>
                    <FontAwesomeIcon icon={item.icon} className={style.sidebarIcon} />
                  </span>
                  <span>{item.name}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Profile Footer ── */}
        <div className={style.profileFooter}>
          <button
            className={style.profileBtn}
            onClick={() => setShowProfilePanel(p => !p)}
            title="Profile"
          >
            <span
              className={style.profileAvatar}
              style={{ background: roleColor }}
            >
              {avatarSrc ? (
                <img src={avatarSrc} alt={fullName} className={style.profileAvatarImg} />
              ) : initials}
            </span>
            <div className={style.profileInfo}>
              <span className={style.profileName}>{fullName}</span>
              <span className={style.profileRole} style={{ color: roleColor }}>{roleLabel}</span>
            </div>
            <FontAwesomeIcon
              icon={faSignOut}
              className={style.profileSignOutIcon}
              title="Sign out"
              onClick={(e) => { e.stopPropagation(); setShowLogoutDialog(true); }}
            />
          </button>

          {/* Expanded profile panel */}
          {showProfilePanel && (
            <div className={style.profilePanel}>
              <div className={style.profilePanelHeader}>
                <span
                  className={style.profilePanelAvatar}
                  style={{ background: roleColor }}
                >
                  {auth.profile_image ? (
                    <img src={auth.profile_image} alt={fullName} className={style.profileAvatarImg} />
                  ) : initials}
                </span>
                <div>
                  <div className={style.profilePanelName}>{fullName}</div>
                  <span className={style.profilePanelBadge} style={{ background: roleColor }}>{roleLabel}</span>
                </div>
              </div>
              {auth.email && (
                <div className={style.profilePanelRow}>
                  <FontAwesomeIcon icon={faEnvelope} className={style.profilePanelIcon} />
                  <span>{auth.email}</span>
                </div>
              )}
              {auth.mobile && (
                <div className={style.profilePanelRow}>
                  <FontAwesomeIcon icon={faPhone} className={style.profilePanelIcon} />
                  <span>{auth.mobile}</span>
                </div>
              )}
              <div className={style.profilePanelDivider} />
              <button
                className={style.profilePanelLink}
                onClick={() => { setShowProfilePanel(false); navigate('/profile'); }}
              >
                <FontAwesomeIcon icon={faIdCard} />
                My Profile
              </button>
              <button
                className={style.profilePanelLink}
                onClick={() => { setShowProfilePanel(false); navigate('/settings'); }}
              >
                <FontAwesomeIcon icon={faGear} />
                Settings
              </button>
              <div className={style.profilePanelDivider} />
              <button
                className={style.profilePanelLogout}
                onClick={() => { setShowProfilePanel(false); setShowLogoutDialog(true); }}
              >
                <FontAwesomeIcon icon={faRightFromBracket} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
